import { Injectable } from '@angular/core';
import { Configuration } from '@content-platform/web-angular-shared-util-configuration';
import { catchError, filter, take } from 'rxjs/operators';
import { Observable, of, forkJoin } from 'rxjs';
import { HttpClient } from '@angular/common/http';

const SOURCE_TYPE = 'FRONT-END';
const EVENT_VERSION = 1.2;

export enum eventTypeEnum {
  SUBMISSION = 'SUBMISSION',
  DEBUG_FLAG = 'DEBUG_FLAG',
  REGISTRATION = 'REGISTRATION',
  GEOLOCATION = 'GEOLOCATION_SUBMISSION',
  USER_EXT_CREATE = 'USER_EXT_CREATE',
  USER_EXT_INVITE = 'USER_EXT_INVITE',
  USER_EXT_GROUP = 'USER_EXT_GROUP',
  USER_EXT_REQUEST = 'USER_EXT_REQUEST',
  USER_LOGGED_IN = 'USER_LOGGED_IN',
  SEARCH_HISTORY = 'SEARCH_HISTORY',
  REDIRECT = 'REDIRECT',
  REGGIE_UPDATE_CLICK = 'REGGIE_UPDATE_CLICK',
  REGGIE_CANCEL_CLICK = 'REGGIE_CANCEL_CLICK',
  REGGIE_SUBMISSION_ERROR_LOG = 'REGGIE_SUBMISSION_ERROR_LOG'
}

export enum actionEnum {
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  SEARCH = 'SEARCH'
}

@Injectable()
export class MegaBeaconService {
  private clientId: string;
  private environment: string;
  private schemaUrl: string;
  private megaBeaconUrl: string;

  constructor(private httpClient: HttpClient) {
    forkJoin(
      Configuration.getAsync('adal').pipe(
        filter(config => !!config),
        take(1)
      ),
      Configuration.getAsync('environment').pipe(
        filter(config => !!config),
        take(1)
      ),
      Configuration.getAsync('megaBeacon').pipe(
        filter(config => !!config),
        take(1)
      )
    ).subscribe(([res1, res2, res3]: Array<any>) => {
      this.clientId = res1.clientId;
      this.environment = res2;
      this.megaBeaconUrl = res3.url;
      this.schemaUrl = res3.schemaUrl;
    });
  }

  /**
   * Logs an event to Mega Beacon
   *
   * @param {string} caller The calling component/service/class
   * @param {eventType} eventType Event type to be logged (SUBMISSION|REGISTRATION|USER_LOGGED_IN)
   * @param {action} action Action to be logged (CREATE|READ|UPDATE|DELETE|SEARCH)
   * @param {ActionParams} params Extra parameters to be logged
   * @param {AdditionalDataParams} additionalData The additional data to log (Optional)
   * @param {schema} schema JSON schema's URL to validate additional data
   * @memberof MegaBeaconService
   */

  log(
    eventType: eventTypeEnum,
    action: actionEnum,
    params: ActionParams,
    jsonSchemaFileName?: string,
    additionalData?: AdditionalDataParams
  ): Observable<Response> {
    const actionParams = this.createActionParams(
      params,
      eventType,
      action,
      jsonSchemaFileName,
      additionalData
    );

    return this.sendData(actionParams).pipe(response => response);
  }

  private createActionParams(
    params: ActionParams,
    eventType: eventTypeEnum,
    action: actionEnum,
    jsonSchemaFileName?: string,
    additionalData?: AdditionalDataParams
  ): AuditEventParams {
    const actionParams: AuditEventParams = {
      events: [
        {
          $schema: `${this.schemaUrl}${jsonSchemaFileName}`,
          event_version: EVENT_VERSION,

          requestor: {
            user: {
              id: params.uuid,
              email: params.userName,
              name: params.name,
              user_type: params.userType
            },
            application: {
              id: this.clientId,
              name: params.activeApplication.toUpperCase(),
              type: SOURCE_TYPE
            }
          },

          source: params.activeApplication.toUpperCase(),
          source_type: SOURCE_TYPE,
          deeplink: params.url,
          env: this.environment,
          action: action,
          event_type: eventType,
          time: new Date().toISOString(),

          additional_data: additionalData
        }
      ]
    };

    return actionParams;
  }

  private sendData(params: AuditEventParams): Observable<Response> {
    return this.httpClient.post<Response>(this.megaBeaconUrl, params).pipe(
      response => {
        return response;
      },
      catchError(error => of(error))
    );
  }
}

/**
 * Parameters for actions to be logged to Mega Beacon
 * Attributes should respect the JSON schema found here:
 * https://confluence.mtvi.com/display/TCP/How+to+send+data
 *
 * @interface AuditEventParams
 */
interface AuditEventParams {
  events: [
    {
      $schema: string;
      event_version: number;

      requestor: {
        user: {
          id: string;
          email: string;
          name: string;
          user_type: string;
        };

        application?: {
          id: string;
          name: string;
          type: string;
        };
      };

      source: string;
      source_type: string;
      deeplink?: string;
      env: string;
      action: string;
      event_type: string;
      time: string;

      additional_data?: {};
    }
  ];
}

/**
 * Response from Mega Beacon request
 *
 * @export
 * @interface Response
 */
export interface Response {
  statusCode: string;
  status: string;
  errorMessage: string;
}

/**
 * Parameters for actions to be logged to Mega Beacon
 *
 * @export
 * @interface ActionParams
 */
export interface ActionParams {
  [key: string]: any;
  first?: number;
  time?: number;
  timeOffset?: number;
}

/**
 * Parameters for additional data to be logged on Mega Beacon
 *
 * @export
 * @interface AdditionalDataParams
 */
export interface AdditionalDataParams {
  [key: string]: any;
}
